import * as React from 'react';
import type { DataValue } from 'react-apollo';
import { graphql } from 'react-apollo';

import gql from 'graphql-tag';

import { Box, StyleSheet, color, css } from '@coursera/coursera-ui';

import type { AutoEnrollmentRow } from 'bundles/internal-enterprise/components/InternalThirdPartyOrganizationPage/AutoEnrollmentEditTriggerModal';
import AutoEnrollmentEditTriggerModal from 'bundles/internal-enterprise/components/InternalThirdPartyOrganizationPage/AutoEnrollmentEditTriggerModal';
import { AutoEnrollmentForm } from 'bundles/internal-enterprise/components/InternalThirdPartyOrganizationPage/AutoEnrollmentForm';
import AutoEnrollmentTable from 'bundles/internal-enterprise/components/InternalThirdPartyOrganizationPage/AutoEnrollmentTable';
import type {
  AutoEnrollmentTriggersQuery,
  AutoEnrollmentTriggersQueryVariables,
} from 'bundles/internal-enterprise/components/InternalThirdPartyOrganizationPage/__generated__/AutoEnrollmentTriggersQuery';
import { TriggerEvent } from 'bundles/internal-enterprise/components/InternalThirdPartyOrganizationPage/constants/AutoEnrollmentConstants';
import {
  createAutoEnrollmentTrigger,
  deleteTrigger,
  updateAutoEnrollmentSetting,
} from 'bundles/internal-enterprise/components/utils/AutoEnrollmentSettingsUtils';
import Button from 'bundles/internal-ui/components/buttons/Button';
import Modal from 'bundles/internal-ui/components/modal/Modal';

import _t from 'i18n!nls/internal-enterprise';

const styles = StyleSheet.create({
  errorMessage: {
    textAlign: 'center',
    color: color.error,
  },
  buttonWrapper: {
    marginBottom: '20px',
  },
});

type State = {
  targetCourseId: string;
  triggerType: TriggerEvent;
  triggeredByCourseId: string;
  saveError?: string;
  updateError?: string;
  showUpdateModal: boolean;
  showDeleteModal: boolean;
  editRow: AutoEnrollmentRow;
};

type PropsFromGraphql = {
  data?: DataValue<AutoEnrollmentTriggersQuery, AutoEnrollmentTriggersQueryVariables>;
};

type PropsFromCaller = {
  program: { id: string; name: string };
};

type Props = PropsFromCaller & PropsFromGraphql;

export class AutoEnrollmentSettingsBody extends React.Component<Props, State> {
  state: State = {
    triggerType: TriggerEvent.JOIN_PROGRAM,
    targetCourseId: '',
    triggeredByCourseId: '',
    showUpdateModal: false,
    showDeleteModal: false,
    editRow: {} as AutoEnrollmentRow,
  };

  isSaveDisabled = () => {
    const { targetCourseId, triggeredByCourseId, triggerType } = this.state;
    if (triggerType === TriggerEvent.JOIN_PROGRAM) {
      return !targetCourseId;
    } else {
      return !targetCourseId || !triggeredByCourseId;
    }
  };

  editTrigger = (trigger: $TSFixMe) => {
    this.setState({ showUpdateModal: true, editRow: trigger });
  };

  openDeleteModal = (trigger: $TSFixMe) => {
    this.setState({ showDeleteModal: true, editRow: trigger });
  };

  deleteAutoEnrollmentRule = () => {
    const { editRow } = this.state;
    const { data } = this.props;
    return deleteTrigger(editRow.id).then(() => {
      this.setState({ showDeleteModal: false });
      data?.refetch();
    });
  };

  handleSave = () => {
    const { triggerType, triggeredByCourseId, targetCourseId } = this.state;
    const { program, data } = this.props;

    return createAutoEnrollmentTrigger(program.id, targetCourseId, triggerType, triggeredByCourseId)
      .then((_) => (_.status >= 500 ? Promise.resolve(_) : _.json()))
      .then((res) => {
        if ('errorCode' in res) {
          this.setState({ saveError: res.message as string });
        } else if ('status' in res) {
          this.setState({
            updateError: _t('Something went wrong! Response status: #{status}', { status: res.status }),
          });
        } else {
          this.setState({ saveError: undefined, targetCourseId: '', triggeredByCourseId: '' });
          data?.refetch();
        }
      });
  };

  handleUpdate = (
    targetProgramId: string,
    targetProduct: string,
    typeName: TriggerEvent,
    definitionId: string,
    id: string
  ) => {
    const { data } = this.props;
    return updateAutoEnrollmentSetting(targetProgramId, targetProduct, typeName, definitionId, id)
      .then((_) => (_.status >= 500 ? Promise.resolve(_) : _.json()))
      .then((res) => {
        if ('errorCode' in res) {
          this.setState({ updateError: res.message });
        } else if ('status' in res) {
          this.setState({
            updateError: _t('Something went wrong! Response status: #{status}', { status: res.status }),
          });
        } else {
          this.setState({ updateError: undefined, showUpdateModal: false });
          data?.refetch();
        }
      });
  };

  render() {
    const { program, data } = this.props;
    const {
      triggerType,
      triggeredByCourseId,
      targetCourseId,
      showUpdateModal,
      editRow,
      saveError,
      updateError,
      showDeleteModal,
    } = this.state;

    const elements = data?.ProgramEnrollmentSettingsV1Resource?.byTargetProgram?.elements || [];
    const triggers = elements.filter((el) => !!el);

    return (
      <div>
        <AutoEnrollmentForm
          handleTargetCourseIdInput={(e) => this.setState({ targetCourseId: (e.target as HTMLInputElement).value })}
          targetCourseId={targetCourseId}
          setTriggerType={(trigger) => this.setState({ triggerType: trigger as TriggerEvent })}
          triggerType={triggerType}
          handleTriggeredByCourseInput={(e) =>
            this.setState({ triggeredByCourseId: (e.target as HTMLInputElement).value })
          }
          triggeredByCourseId={triggeredByCourseId}
        />
        {saveError && (
          <div {...css(styles.errorMessage)}>
            {_t('Unable to add auto enrollment rule')} {saveError}
          </div>
        )}
        <Box alignItems="end" rootClassName={styles.buttonWrapper}>
          <Button btnStyle="primary" disabled={this.isSaveDisabled()} onClick={this.handleSave} text={_t('Save')} />
        </Box>
        {program && (
          <AutoEnrollmentTable
            enrollmentSettings={triggers}
            program={program}
            deleteTrigger={this.openDeleteModal}
            editTrigger={this.editTrigger}
          />
        )}
        {showUpdateModal && (
          <AutoEnrollmentEditTriggerModal
            row={editRow}
            closeModal={() => this.setState({ showUpdateModal: false, updateError: undefined })}
            programId={program.id}
            showModal={showUpdateModal}
            handleUpdate={this.handleUpdate}
            error={updateError}
          />
        )}
        <Modal open={showDeleteModal} title={_t('Delete Rule?')}>
          <Box justifyContent="center">
            <Button className="m-r-2" text={_t('Yes')} onClick={this.deleteAutoEnrollmentRule} />
            <Button className="m-l-2" text={_t('No')} onClick={() => this.setState({ showDeleteModal: false })} />
          </Box>
        </Modal>
      </div>
    );
  }
}

export default graphql<
  PropsFromCaller,
  AutoEnrollmentTriggersQuery,
  AutoEnrollmentTriggersQueryVariables,
  PropsFromGraphql
>(
  gql`
    query AutoEnrollmentTriggersQuery($id: String!) {
      ProgramEnrollmentSettingsV1Resource {
        byTargetProgram(programId: $id) {
          elements {
            id

            enrollmentTargetProgram {
              id
            }

            enrollmentTargetCourse {
              id
              slug
              name
            }
            triggerEventJoinedProgram {
              id
              metadata {
                slug
                name
              }
            }
            triggerEventCompletedCourse {
              slug
              name
              id
            }
            triggerEventEnrolledCourse {
              slug
              name
              id
            }
          }
        }
      }
    }
  `,
  {
    options: (props) => ({
      variables: { id: props.program.id },
    }),
  }
)(AutoEnrollmentSettingsBody);
